import type { PublicCatalogProduct } from "./types.js";

const maximumWholeEgp = 90_000_000_000_000n;
const wholeEgp = /^(0|[1-9]\d*)$/;

export type CatalogPriceFilterResult =
  | {
      valid: true;
      minPriceMinor?: string;
      maxPriceMinor?: string;
    }
  | { valid: false; error: "PRICE_VALUE_INVALID" | "PRICE_RANGE_INVERTED" };

export const parseCatalogPriceFilter = (input: {
  minEgp?: string;
  maxEgp?: string;
}): CatalogPriceFilterResult => {
  const parse = (value: string | undefined) => {
    const normalized = value?.trim();
    if (!normalized) return undefined;
    if (!wholeEgp.test(normalized)) return null;
    const amount = BigInt(normalized);
    return amount <= maximumWholeEgp ? amount : null;
  };
  const minimum = parse(input.minEgp);
  const maximum = parse(input.maxEgp);
  if (minimum === null || maximum === null)
    return { valid: false, error: "PRICE_VALUE_INVALID" };
  if (minimum !== undefined && maximum !== undefined && maximum < minimum)
    return { valid: false, error: "PRICE_RANGE_INVERTED" };
  return {
    valid: true,
    ...(minimum === undefined ? {} : { minPriceMinor: `${minimum}00` }),
    ...(maximum === undefined ? {} : { maxPriceMinor: `${maximum}00` }),
  };
};

export const pricePresentation = (product: PublicCatalogProduct) => {
  const envelope = product.pricingEnvelope;
  if (product.quoteRequired || envelope?.mode === "QUOTE_REQUIRED")
    return { kind: "QUOTE_REQUIRED" as const };
  if (!envelope?.minAmountMinor || !envelope.maxAmountMinor)
    return { kind: "UNAVAILABLE" as const };
  if (envelope.mode === "FIXED")
    return {
      kind: "FIXED" as const,
      minAmountMinor: envelope.minAmountMinor,
      maxAmountMinor: envelope.maxAmountMinor,
    };
  if (envelope.minAmountMinor === envelope.maxAmountMinor)
    return {
      kind: "FROM" as const,
      minAmountMinor: envelope.minAmountMinor,
      maxAmountMinor: envelope.maxAmountMinor,
    };
  return {
    kind: "RANGE" as const,
    minAmountMinor: envelope.minAmountMinor,
    maxAmountMinor: envelope.maxAmountMinor,
  };
};

export const formatMinorCurrency = (
  locale: "en" | "ar",
  value: string,
  alwaysShowMinorUnits = false,
) => {
  const amount = BigInt(value);
  const major = amount / 100n;
  const fraction = amount % 100n;
  const number = new Intl.NumberFormat(locale === "ar" ? "ar-EG" : "en-EG", {
    useGrouping: true,
    maximumFractionDigits: 0,
  }).format(major);
  const fractionText =
    fraction === 0n && !alwaysShowMinorUnits
      ? ""
      : `${locale === "ar" ? "٫" : "."}${new Intl.NumberFormat(
          locale === "ar" ? "ar-EG" : "en-EG",
          {
            useGrouping: false,
            minimumIntegerDigits: 2,
          },
        ).format(fraction)}`;
  return locale === "ar"
    ? `${number}${fractionText} EGP`
    : `EGP ${number}${fractionText}`;
};
